import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { User } from "@shared/types";
import { apiRequest } from "@/lib/queryClient";
import { storeApsToken } from "@/lib/token-client";
import React from "react";
import CONFIG from "../lib/config";

export function useAuth() {
  const queryClient = useQueryClient();
  
  // Check if we're on Vercel deployment by checking the hostname
  const isVercelDeployment = typeof window !== 'undefined' && window.location.hostname.includes('vercel.app');
  
  // Check if we're on login page - don't perform auth check on login page
  const isLoginPage = typeof window !== 'undefined' && (window.location.pathname === '/login' || window.location.pathname.startsWith('/api/auth/login'));
  
  // Check if we're on a page that should handle session expiry gracefully
  const shouldHandleSessionExpiry = typeof window !== 'undefined' && 
    (window.location.pathname === '/login' || 
     window.location.pathname === '/myhome' || 
     window.location.pathname === '/');
  
  // TEMPORARY FIX: Force authenticated state for development to resolve login loops
  const FORCE_AUTH_FOR_DEV = false; // Disable forced auth now that Render backend is ready
  
  // Only log in verbose mode
  if (CONFIG.DEBUG_LEVEL === 'verbose') {
    console.log('🔧 AUTH CONFIG: FORCE_AUTH_FOR_DEV =', FORCE_AUTH_FOR_DEV);
  }
  
  // Get session ID from URL params, cookies, or localStorage
  const getSessionId = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const urlSessionId = urlParams.get('sessionId');
      const storedSessionId = localStorage.getItem('sessionId');

      // Priority: URL > localStorage
      if (urlSessionId) {
        localStorage.setItem('sessionId', urlSessionId);
        return urlSessionId;
      }
      
      return storedSessionId;
    }
    return null;
  };
  
  // Presence of session cookie indicates we may already have a server-side session,
  // even if we don't have a cross-domain sessionId in URL/localStorage.
  const hasSessionCookie = typeof window !== 'undefined' && document.cookie.includes('connect.sid=');

  // Check authentication status with session ID
  const { data: authResponse, isLoading, error } = useQuery<{user: User | null, isAuthenticated: boolean}>({
    queryKey: ["/api/auth/user", getSessionId()],
    // Only run auth check when we have evidence it can succeed (session cookie or sessionId).
    // This prevents first-time visitors from waiting on a slow auth endpoint.
    enabled: isLoginPage ? getSessionId() !== null : (hasSessionCookie || getSessionId() !== null),
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Add timeout for authentication check (10 seconds)
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const sessionId = getSessionId();
        const url = sessionId ? `/api/auth/user?sessionId=${sessionId}` : '/api/auth/user';
        
        // Add timeout wrapper for authentication request
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Authentication request timeout')), 10000);
        });
        
        const response = await Promise.race([
          apiRequest(url) as Promise<{user: User | null, isAuthenticated: boolean}>,
          timeoutPromise
        ]);
        
        // 添加调试信息查看后端返回的数据
        console.log('🔧 AUTH RESPONSE DEBUG:');
        console.log('  response:', JSON.stringify(response, null, 2));
        console.log('  isAuthenticated:', response?.isAuthenticated);
        console.log('  user:', JSON.stringify(response?.user, null, 2));
        console.log('  sessionId:', sessionId);
        
        // Store session ID if available and immediately clean URL
        if (sessionId && typeof window !== 'undefined') {
          localStorage.setItem('sessionId', sessionId);
          
          // 修复OAuth回调检测 - 检测OAuth回调后的重定向
          const url = new URL(window.location.href);
          const hasOAuthParams = url.searchParams.has('code') || url.searchParams.has('state');
          const hasSessionIdInUrl = url.searchParams.has('sessionId');
          const hasSessionIdInStorage = localStorage.getItem('sessionId') !== null;
          const hasSessionId = hasSessionIdInUrl || hasSessionIdInStorage;
          const loginFlowStarted = localStorage.getItem('loginFlowStarted') === 'true';
          const isMyHomeWithSessionId = window.location.pathname === '/myhome' && hasSessionId;
          
          // OAuth回调检测：有OAuth参数 OR (有sessionId且登录流程已开始) OR (在myhome页面且有sessionId)
          const isOAuthCallback = hasOAuthParams || 
            (hasSessionId && loginFlowStarted) ||
            (hasSessionId && isMyHomeWithSessionId);
          
          // 保存OAuth回调状态
          const wasOAuthCallback = isOAuthCallback;
          
          console.log('🔍 OAUTH CALLBACK DETECTION:');
          console.log('  hasOAuthParams:', hasOAuthParams);
          console.log('  hasSessionIdInUrl:', hasSessionIdInUrl);
          console.log('  hasSessionIdInStorage:', hasSessionIdInStorage);
          console.log('  hasSessionId:', hasSessionId);
          console.log('  pathname:', window.location.pathname);
          console.log('  isOAuthCallback:', isOAuthCallback);
          console.log('  wasOAuthCallback:', wasOAuthCallback);
          console.log('  urlHref:', window.location.href);
          console.log('  searchParams:', window.location.search);
          
          // 清理URL参数
          if (url.searchParams.has('sessionId')) {
            url.searchParams.delete('sessionId');
            window.history.replaceState(null, '', url.toString());
          }
          
          if (isOAuthCallback) {
            localStorage.setItem('loginFlowStarted', 'true');
            if (CONFIG.DEBUG_LEVEL === 'verbose') {
              console.log('🔐 AUTH: Set loginFlowStarted for OAuth callback');
            }
          }
          
        // 清理登录流程标记和session
        console.log('🔍 AUTH CLEANUP DEBUG:');
        console.log('  wasOAuthCallback:', wasOAuthCallback);
        console.log('  hasSessionId:', hasSessionId);
        console.log('  isAuthenticated:', isAuthenticated);
        console.log('  loginFlowStarted:', localStorage.getItem('loginFlowStarted'));
        
        // 如果认证失败，清理所有登录相关状态
        if (!isAuthenticated && !wasOAuthCallback) {
          console.log('🔐 AUTH: Authentication failed, cleaning up session and login state');
          localStorage.removeItem('loginFlowStarted');
          localStorage.removeItem('sessionId');
          localStorage.removeItem('autodeskSessionId');
          // 清理所有可能的session存储
          Object.keys(localStorage).forEach(key => {
            if (key.includes('session') || key.includes('auth') || key.includes('login')) {
              localStorage.removeItem(key);
            }
          });
        } else if (!wasOAuthCallback) {
          // 只有在非OAuth回调且认证成功的情况下才清理登录流程标记
          const hadLoginFlow = localStorage.getItem('loginFlowStarted') === 'true';
          localStorage.removeItem('loginFlowStarted');
          if (hadLoginFlow && CONFIG.DEBUG_LEVEL === 'verbose') {
            console.log('🔐 AUTH: Cleared loginFlowStarted after successful authentication');
          }
        }
        }
        
        return response;
      } catch (err: any) {
        if (CONFIG.DEBUG_LEVEL !== 'none') {
          console.log('Auth check failed:', err.message);
        }
        
        // Handle timeout or network errors - redirect to login
        const isTimeout = err.message?.includes('timeout') || err.message?.includes('NetworkError') || err.message?.includes('Failed to fetch');
        const isAuthError = err.message?.includes('401') || err.message?.includes('Unauthorized');
        
        if ((isTimeout || isAuthError) && shouldHandleSessionExpiry) {
          if (CONFIG.DEBUG_LEVEL !== 'none') {
            console.log(`🔧 ${isTimeout ? 'Authentication timeout' : 'Session expired'}, redirecting to login`);
          }
          if (typeof window !== 'undefined') {
            // Clear all auth-related storage
            localStorage.removeItem('sessionId');
            localStorage.removeItem('autodeskSessionId');
            localStorage.removeItem('loginFlowStarted');
            // Redirect to login after a short delay to show error message
            setTimeout(() => {
              window.location.href = '/login';
            }, 1000);
          }
        }
        
        return { user: null, isAuthenticated: false };
      }
    },
  });

  // Extract user from auth response (backend now returns { user, isAuthenticated })
  const user = authResponse?.user || null;
  
  // Store APS token for cross-domain requests when user is authenticated
  React.useEffect(() => {
    if (user?.authToken && typeof window !== 'undefined') {
      console.log('🔐 Storing APS token for cross-domain requests');
      storeApsToken(user.authToken);
    }
  }, [user?.authToken]);

  // Clean up unused devMockUser since we're using backend response
  // const devMockUser removed - using actual backend response

  // If we're on login page and no sessionId, return unauthenticated state without making API calls
  if (isLoginPage && getSessionId() === null) {
    return {
      user: null,
      permissions: null,
      isLoading: false,
      error: null,
      isAuthenticated: false,
      isAppAdmin: false,
      isAccountAdmin: false,
      isProjectAdmin: false,
      isTendererOnly: false,
      canEditPackages: false,
      isEditor: false,
      oauthLogin: () => {
        const backendUrl = import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? 'https://api.procurement.tectonixai.com' : 'http://localhost:5001');
        window.location.href = `${backendUrl}/api/auth/login`;
      },
      tendererLogin: async () => {},
      tryTokenLogin: async () => {},
      logout: () => {
        queryClient.clear();
        const backendUrl = import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? 'https://api.procurement.tectonixai.com' : 'http://localhost:5001');
        window.location.href = `${backendUrl}/api/auth/logout`;
      },
    };
  }

  // If there's a 401 error, immediately return unauthenticated state
  if (error && error.message?.includes('401')) {
    return {
      user: null,
      permissions: null,
      isLoading: false,
      error: null, // Don't expose 401 error to components
      isAuthenticated: false,
      isAppAdmin: false,
      isAccountAdmin: false,
      isProjectAdmin: false,
      isTendererOnly: false,
      canEditPackages: false,
      isEditor: false,
      oauthLogin: () => {
        const backendUrl = import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? 'https://api.procurement.tectonixai.com' : 'http://localhost:5001');
        window.location.href = `${backendUrl}/api/auth/login`;
      },
      tendererLogin: async () => {},
      tryTokenLogin: async () => {},
      logout: () => {
        queryClient.clear();
        const backendUrl = import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? 'https://api.procurement.tectonixai.com' : 'http://localhost:5001');
        window.location.href = `${backendUrl}/api/auth/logout`;
      },
    };
  }

  // Get user permissions
  const { data: permissions } = useQuery({
    queryKey: ["/api/auth/permissions"],
    enabled: !!user,
    retry: false,
    queryFn: async () => {
      try {
        return await apiRequest('/api/auth/permissions');
      } catch (err: any) {
        // Handle network errors gracefully
        console.warn('Permissions check failed:', err.message);
        return {
          level: 'none',
          canAccessAllProjects: false,
          canAccessCompanyProjects: false,
          canAccessSpecificProject: false,
          isTendererOnly: false,
        };
      }
    },
  });

  // OAuth login for admins
  const oauthLogin = () => {
    // Use the configured backend URL
    const backendUrl = import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? 'https://api.procurement.tectonixai.com' : 'http://localhost:5001');
    
    // Debug: Log the current location values
    console.log('🔍 OAuth Login Debug:', {
      windowLocationOrigin: window.location.origin,
      windowLocationHref: window.location.href,
      windowLocationHostname: window.location.hostname,
      backendUrl,
      isVercel: window.location.hostname.includes('vercel.app'),
      isFeatureBranch: window.location.hostname.includes('-git-')
    });
    
    // Fix: Use current location to construct the return URL
    let returnUrl;
    if (window.location.hostname.includes('vercel.app')) {
      // For Vercel deployment, use current domain (supports feature branches)
      returnUrl = window.location.origin + "/myhome";
    } else {
      // For local development, use window.location.origin
      returnUrl = window.location.origin + "/myhome";
    }
    
    const loginUrl = `${backendUrl}/api/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    
    console.log('🔧 Constructed login URL:', loginUrl);
    console.log('🔧 Return URL will be:', returnUrl);
    window.location.href = loginUrl;
  };

  // Logout function
  const logout = async () => {
    console.log('🔐 LOGOUT: Starting complete logout process');
    
    // 检查是否需要强制清除Autodesk OAuth状态
    const needsForceLogout = localStorage.getItem('autodesk_oauth_remembered') === 'true';
    
    if (needsForceLogout) {
      console.log('🔐 FORCE LOGOUT: 检测到Autodesk OAuth状态需要强制清除');
      // 重定向到强制logout页面
      window.location.href = '/force-logout.html';
      return;
    }
    
    // Clear React Query cache first
    queryClient.clear();
    queryClient.removeQueries();
    queryClient.cancelQueries();
    
    // Clear all authentication data
    if (typeof window !== 'undefined') {
      // Clear all known authentication keys
      const authKeys = [
        'sessionId',
        'loginFlowStarted', 
        'adminToken',
        'adminUser',
        'tendererAuth',
        'acc_token',
        'acc_refresh_token', 
        'acc_user_data',
        'selectedProject',
        'hub-data-updated'
      ];
      
      // Remove all known auth keys
      authKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      
      // Clear any other potential auth-related storage with comprehensive pattern matching
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes('auth') || 
          key.includes('token') || 
          key.includes('session') ||
          key.includes('user') ||
          key.includes('login') ||
          key.includes('acc') ||
          key.includes('admin')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Clear sessionStorage as well
      const sessionKeysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (
          key.includes('auth') || 
          key.includes('token') || 
          key.includes('session') ||
          key.includes('user') ||
          key.includes('login') ||
          key.includes('acc') ||
          key.includes('admin')
        )) {
          sessionKeysToRemove.push(key);
        }
      }
      sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      // Force clear all cookies
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      // Clear any cached data in memory
      if (window.caches) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
      
      console.log('🔐 LOGOUT: Cleared all local storage, session storage, cookies, and cache');
    }
    
    // Always call backend logout API to clear server-side session and ACC token cache
    try {
      const backendUrl = import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? 'https://api.procurement.tectonixai.com' : 'http://localhost:5001');
      await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'GET',
        credentials: 'include', // Include cookies for session clearing
      });
      console.log('✅ Backend logout API called successfully - cleared session and ACC token cache');
    } catch (error) {
      console.error('Backend logout failed:', error);
      // Continue with frontend logout even if backend fails
    }
    
    // Force clear any remaining cookies by setting them to expire
    if (typeof document !== 'undefined') {
      // Clear all cookies by setting them to expire in the past
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      console.log('🔐 LOGOUT: Cleared all cookies');
    }
    
    // Add a small delay to ensure all cleanup is complete
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Force clear all browser cache and reload
    if (window.caches) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    
    // Force clear all storage one more time - this is critical!
    localStorage.clear();
    sessionStorage.clear();
    
    // Explicitly remove sessionId from URL if it exists
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('sessionId')) {
      currentUrl.searchParams.delete('sessionId');
      window.history.replaceState({}, '', currentUrl.toString());
    }
    
    // Redirect to login page with cache-busting parameter and force reload
    const timestamp = Date.now();
    const loginUrl = window.location.hostname.includes('vercel.app') 
      ? window.location.origin + `/login?t=${timestamp}&logout=true&cleared=true`
      : `/login?t=${timestamp}&logout=true&cleared=true`;
    
    // Force a hard reload to clear all browser cache
    // 使用更强制的清理方式
    console.log('🔐 LOGOUT: 执行强制清理和重定向');
    
    // 最后一步：完全重置应用状态
    if (typeof window !== 'undefined') {
      // 清除所有可能的缓存
      try {
        // 清除IndexedDB
        if (window.indexedDB) {
          const databases = await indexedDB.databases();
          for (const db of databases) {
            indexedDB.deleteDatabase(db.name);
          }
        }
      } catch (e) {
        console.log('IndexedDB清理失败:', e);
      }
      
      // 强制清除所有存储
      localStorage.clear();
      sessionStorage.clear();
      
      // 清除所有cookies（包括子域名）
      document.cookie.split(";").forEach(function(c) { 
        const cookie = c.replace(/^ +/, "").split("=")[0];
        document.cookie = cookie + "=;expires=" + new Date().toUTCString() + ";path=/;domain=" + window.location.hostname;
        document.cookie = cookie + "=;expires=" + new Date().toUTCString() + ";path=/;domain=." + window.location.hostname;
      });
      
      console.log('🔐 LOGOUT: 所有数据已清除，正在重定向...');
    }
    
    // 使用replace而不是assign，确保无法返回
    // 最后一步：完全重置应用状态
    console.log('🔐 LOGOUT: 执行最终清理和重定向');
    
    // 强制清除所有可能的认证数据
    if (typeof window !== 'undefined') {
      // 清除所有可能的缓存
      try {
        // 清除IndexedDB
        if (window.indexedDB) {
          const databases = await indexedDB.databases();
          for (const db of databases) {
            indexedDB.deleteDatabase(db.name);
          }
        }
      } catch (e) {
        console.log('IndexedDB清理失败:', e);
      }
      
      // 强制清除所有存储
      localStorage.clear();
      sessionStorage.clear();
      
      // 清除所有cookies（包括子域名）
      document.cookie.split(";").forEach(function(c) { 
        const cookie = c.replace(/^ +/, "").split("=")[0];
        document.cookie = cookie + "=;expires=" + new Date().toUTCString() + ";path=/;domain=" + window.location.hostname;
        document.cookie = cookie + "=;expires=" + new Date().toUTCString() + ";path=/;domain=." + window.location.hostname;
      });
      
      console.log('🔐 LOGOUT: 所有数据已清除，正在重定向...');
    }
    
    // 使用replace而不是assign，确保无法返回
    window.location.replace(loginUrl);
  };

  // Tenderer login with email and code
  const tendererLoginMutation = useMutation({
    mutationFn: async ({ email, accessCode }: { email: string; accessCode: string }) => {
      const response = await apiRequest('/api/auth/tenderer/login', {
        method: 'POST',
        body: JSON.stringify({ email, accessCode }),
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
  });

  // Try token login
  const tryTokenLoginMutation = useMutation({
    mutationFn: async (token: string) => {
      const response = await apiRequest('/api/auth/token/login', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
  });

  // Use actual query results instead of hardcoded demo data

  // Calculate derived permissions using enhanced logic
  const isAuthenticated = authResponse?.isAuthenticated === true && !!user;
  // Set permission level to app_admin for development if not already set
  const userWithPermissions = user ? {
    ...user,
    permissionLevel: (user as any).permissionLevel || 'app_admin'
  } : null;
  
  const isAppAdmin = userWithPermissions && userWithPermissions.permissionLevel === 'app_admin';
  const isAccountAdmin = userWithPermissions && (userWithPermissions.permissionLevel === 'account_admin' || isAppAdmin);  
  const isProjectAdmin = userWithPermissions && (userWithPermissions.permissionLevel === 'project_admin' || isAccountAdmin);
  const isTendererOnly = userWithPermissions && userWithPermissions.permissionLevel === 'tenderer';
  const canEditPackages = (isProjectAdmin || isAppAdmin) && !isTendererOnly;
  // CRITICAL FIX: Map permission levels to editor status
  const isEditor = userWithPermissions && 
    ['app_admin', 'account_admin', 'project_admin'].includes(userWithPermissions.permissionLevel);
  
  // Debug logging - only in verbose development mode
  if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_VERBOSE === 'true') {
    console.log('🔧 AUTH STATE:', { 
      isAuthenticated, 
      user: userWithPermissions, 
      isAppAdmin, 
      isEditor,
      permissionLevel: userWithPermissions?.permissionLevel 
    });
  }

  return {
    user: userWithPermissions,
    permissions,
    isLoading,
    error,
    isAuthenticated,
    isAppAdmin,
    isAccountAdmin,
    isProjectAdmin,
    isTendererOnly,
    canEditPackages,
    isEditor,
    oauthLogin,
    tendererLogin: tendererLoginMutation.mutateAsync,
    tryTokenLogin: tryTokenLoginMutation.mutateAsync,
    logout,
  };
}